import { uniqWith } from "lodash";
import {
  Institution,
  Network,
  Program,
  ProgramMembership,
  ProgramRole,
  Sponsor,
  User
} from "../API";
import { Role } from "../features/Auth/roles";
import {
  selectCurrentUser,
  selectUserRoles
} from "../features/Users/usersSlice";
import { PGYSelectFields, ProgramRoleSelectFields } from "./constants";
import { useAppSelector } from "./hooks";
import { useAllPrograms } from "../features/Programs/programUtils";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryStatus } from "./types";

export const stringOfRoleOption = (programRole: ProgramRole): string => {
  const field = ProgramRoleSelectFields.find(
    (field) => field.value === programRole
  );
  return field?.label || "";
};

export const stringOfPGYOption = (pgy: number): string => {
  const pgyField = PGYSelectFields.find((field) => field.value === pgy);
  return pgyField?.label || "";
};

export const formatTrainingLevel = (
  yearLabelPrefix: string | null | undefined,
  yearInStage: number | null | undefined
): string => {
  if (!yearLabelPrefix || yearInStage == null) return "";
  return `${yearLabelPrefix}-${yearInStage}`;
};

export const formatPgyAndStage = (
  pgy: number | null | undefined,
  yearLabelPrefix: string | null | undefined,
  yearInStage: number | null | undefined,
  stageCode?: string | null
): string => {
  const stageLabel = formatTrainingLevel(yearLabelPrefix, yearInStage);
  const pgyLabel = pgy != null ? `PGY-${pgy}` : "";

  // Medical students (UME) never show PGY — just the stage label, e.g. "MS-3".
  if (stageCode === "UME") return stageLabel;

  // Fellows show both when the fellow year is known: "PGY-7 (Fellow Yr-2)".
  if (stageCode === "FELLOWSHIP" && pgyLabel && stageLabel) {
    return `${pgyLabel} (${stageLabel})`;
  }

  // GME residents show the stage's own year (year_in_stage → "PGY-{year}").
  // Legacy / no-stage rows fall back to the raw PGY. (Fellows handled above.)
  return stageLabel || pgyLabel;
};

// Numeric sort key for the "Clinical Year" column: stage progression, then the
// year shown for that stage, with fellow year as a tiebreak. Blanks sort last.
export const clinicalYearSortKey = (
  parts: {
    pgy?: number | null;
    yearInStage?: number | null;
    stage?: { sortOrder?: number | null; code?: string | null } | null;
  } | null
): number => {
  if (!parts) return Number.MAX_SAFE_INTEGER;
  const { pgy, yearInStage, stage } = parts;
  if (pgy == null && yearInStage == null) return Number.MAX_SAFE_INTEGER;

  const stageRank = stage?.sortOrder != null ? stage.sortOrder : 9998;
  let primary = 0;
  let secondary = 0;
  if (stage?.code === "UME") {
    primary = yearInStage ?? 0;
  } else if (stage?.code === "FELLOWSHIP") {
    primary = pgy ?? 0;
    secondary = yearInStage ?? 0;
  } else {
    // GME reads its stage year (year_in_stage); legacy rows fall back to PGY.
    primary = yearInStage ?? pgy ?? 0;
  }
  return stageRank * 10000 + primary * 100 + secondary;
};

export const returnIdsOfProgramsThatUserAdmins = (
  user: User | undefined
): string[] => {
  const toReturn: string[] = [];
  if (!user) return toReturn;
  const usersProgramMemberships = user.programMemberships?.items;
  if (usersProgramMemberships && usersProgramMemberships.length > 0) {
    for (const programMembership of usersProgramMemberships) {
      if (
        programMembership?.programRole === ProgramRole.ProgramAdmin &&
        !programMembership.endDate
      ) {
        toReturn.push(programMembership.programId);
      }
    }
  }
  return toReturn;
};

export const getProgramMembershipsAsProgramAdmin = (user: User | undefined) => {
  const programMemberships = user?.programMemberships?.items;
  const programMembershipsAsProgramAdmin = programMemberships?.filter(
    (program) =>
      program &&
      program.programRole == ProgramRole.ProgramAdmin &&
      // Membership is active
      !program.endDate
  );
  const hasMultipleProgramsAsProgramAdmin =
    programMembershipsAsProgramAdmin &&
    programMembershipsAsProgramAdmin.length > 1;
  return {
    programs: programMemberships,
    programMembershipsAsProgramAdmin,
    hasMultipleProgramsAsProgramAdmin
  };
};

type UserAdministeredEntities = {
  canAdministerMultipleInstitutions: boolean;
  canAdministerMultiplePrograms: boolean;
  programs?: Program[] | null;
  institutions?: Institution[] | null;
};
export const getProgramsCurrentUserCanAdminister =
  (): UserAdministeredEntities => {
    const currentUser = useAppSelector(selectCurrentUser);
    const currentUserRoles = useAppSelector(selectUserRoles);
    const { programs } = useAllPrograms();
    return getProgramsUserCanAdminister(
      currentUser,
      currentUserRoles,
      programs
    );
  };

export const usersActionableRoleInProgram = (
  user: User,
  program: Program
): ProgramRole | undefined => {
  const usersMembershipsToProgram = user.programMemberships?.items?.filter(
    (membership) => membership?.programId === program.id
  );
  const usersRolesInProgram = usersMembershipsToProgram?.map(
    (membership) => membership?.programRole
  );
  if (usersRolesInProgram?.includes(ProgramRole.Attending)) {
    return ProgramRole.Attending;
  }
  if (usersRolesInProgram?.includes(ProgramRole.Trainee)) {
    return ProgramRole.Trainee;
  }
  return;
};

export const getProgramsUserCanAdminister = (
  user?: User,
  roles?: Role[],
  allPrograms?: Program[] | null
): UserAdministeredEntities => {
  let programs: Program[] | null | undefined = [],
    institutions: Institution[] | null | undefined = [];
  if (!!user && !!roles && !!allPrograms) {
    if (roles?.includes(Role.SystemAdmin)) {
      programs = allPrograms;
    } else if (roles?.includes(Role.ProgramAdmin)) {
      const programMemberships = user?.programMemberships?.items;
      const activeProgramMemberships = programMemberships?.filter(
        (pm) => pm && !pm.endDate
      );
      const adminProgramMemberships = activeProgramMemberships?.filter(
        (pm) => pm && pm.programRole == ProgramRole.ProgramAdmin
      );
      const adminPrograms = allPrograms.filter((p) =>
        adminProgramMemberships?.find((apm) => apm?.programId == p.id)
      );
      programs = adminPrograms;
    }
  }

  institutions = uniqWith(
    programs?.filter((p) => !!p.institution).map((p) => p.institution!),
    (a, b) => !!a && !!b && a.id == b.id
  ).sort((a, b) => {
    if (!a?.name || !b?.name) {
      return 1;
    } else if (a?.name > b?.name) {
      return 1;
    } else {
      return -1;
    }
  });

  const canAdministerMultiplePrograms = programs.length > 1;
  const canAdministerMultipleInstitutions = institutions.length > 1;

  return {
    canAdministerMultiplePrograms,
    canAdministerMultipleInstitutions,
    programs,
    institutions
  };
};

export const containsOnly = <ArrayElement>(
  fullArray: ArrayElement[],
  targetArray: ArrayElement[]
) => {
  return fullArray.every((fullArrayElement) =>
    targetArray.includes(fullArrayElement)
  );
};

export const valueWithoutUpdate = (value: any) => {
  return JSON.parse(JSON.stringify(value));
};

export const fetchUniqueSponsorsFromProgramMemberships = async (
  programMemberships: (ProgramMembership | null)[],
  getNetworksByProgram: (programId: string) => Promise<Network[] | null>
) => {
  const uniqueSponsors: Sponsor[] = [];

  for (const programMembership of programMemberships) {
    if (!programMembership) {
      console.error(
        "programMembership is not available in programMemberships list:",
        programMemberships
      );
      return uniqueSponsors;
    }
    const { programId } = programMembership;
    if (!programId) {
      console.error(
        "programId not set for programMembership:",
        programMembership
      );
      return uniqueSponsors;
    }
    const networksAssocatiatedWithProgram = await getNetworksByProgram(
      programId
    );
    if (!networksAssocatiatedWithProgram) {
      console.error("there are no networks associated with the program.");
      return uniqueSponsors;
    }

    for (const network of networksAssocatiatedWithProgram) {
      if (
        !uniqueSponsors.some((sponsor) => sponsor.id === network.sponsor?.id) &&
        network.sponsor
      ) {
        uniqueSponsors.push(network.sponsor);
      }
    }
  }
  return uniqueSponsors;
};

export const usersActiveProgramMemberships = (user: User) => {
  const membershipItems = Array.isArray(user.programMemberships?.items)
    ? user.programMemberships?.items
    : [];
  const usersProgramMemberships = membershipItems.flatMap((membership) =>
    membership ? [membership] : []
  );
  const usersActiveProgramMemberships = usersProgramMemberships?.filter(
    (membership) => membership && !membership.endDate
  );
  return usersActiveProgramMemberships;
};

export const createDashboardLinkText = (initialText: string) => {
  return `${initialText} Reports`;
};

export const getBaseEndpoint = () => {
  // 1) Explicit override (best for CI, dev tunnels, or nonstandard hosts)
  const explicit = process.env.REACT_APP_API_BASE_URL;
  if (explicit) {
    return explicit.endsWith("/") ? explicit : `${explicit}/`;
  }

  // 2) Cloud envs (production, staging, dev, etc.)
  //    REACT_APP_ENV is already in this project; use api[-ENV].simpl.org
  const envName = process.env.REACT_APP_ENV?.trim();
  if (envName && envName !== "local") {
    const host =
      envName === "production" ? "https://api.simpl.org/" : `https://api-${envName}.simpl.org/`;
    return host;
  }

  // 3) Local default
  return "http://localhost:3001/api/";
};

export const getActiveProgramAdminedProgramIds = (currentUser: User) => {
  return currentUser?.programMemberships?.items
    ?.map((programMembership) => {
      if (
        programMembership?.programRole === "ProgramAdmin" &&
        !programMembership.endDate
      ) {
        return programMembership.programId;
      }
    })
    .flatMap((programId) => (programId ? [programId] : []));
};

/**
 * Returns true when the user has at least one active program membership
 * with `programRole === "Trainee"` or `programRole === "Attending"`.
 *
 * This is the SPA's "can this person see and use the Assessments flow?"
 * predicate, mirroring the mobile clients' `isTrainee() || isAttending()`
 * check (see simpl2-android `UserDomain.kt` and simpl2-ios
 * `UserDomain.swift`). SysAdmins / ProgramAdmins without a Trainee or
 * Attending membership do not get assessment access — matching the
 * pre-rework Cognito `Users`-group gate, which was kept in sync with
 * exactly this signal by `update-program-memberships`.
 */
export const hasActiveTraineeOrAttendingMembership = (
  user: User | null | undefined
): boolean => {
  if (!user) return false;
  const memberships = user.programMemberships?.items ?? [];
  return memberships.some(
    (m) =>
      !!m &&
      !m.endDate &&
      (m.programRole === ProgramRole.Trainee ||
        m.programRole === ProgramRole.Attending)
  );
};

export function useAPI<TInput, TOutput>(
  apiFunction: (input: TInput) => Promise<TOutput>,
  input?: TInput
) {
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TOutput | null>(null);

  const execute = async (input: TInput) => {
    setStatus("loading");
    setError(null);
    try {
      const results = await apiFunction(input);
      setData(results);
      setStatus("succeeded");
    } catch (error) {
      console.error(`error executing API function:`, error);
      setError(`Error fetching data.`);
      setStatus("failed");
    }
  };

  const executeDelayed = useCallback(
    debounce((input: TInput) => {
      execute(input);
    }, 500),
    []
  );

  useEffect(() => {
    if (input) {
      executeDelayed(input);
    }
  }, [input]);

  return {
    data,
    status,
    error,
    execute
  };
}